import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { listWineGrapes } from "../../utils/apiClient";

const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function findExact(hits, token) {
  const key = normalizeKey(token);
  if (!key) return null;
  return (
    hits.find((g) => normalizeKey(g.name) === key) ||
    hits.find((g) => normalizeKey(g.canonical_name) === key) ||
    null
  );
}

/**
 * Closed-list grape picker: searches DB after 3 letters.
 * Enter / Comma / click commits a chip; Space is allowed in multi-word names.
 */
function GrapeChipsField({ value = [], onChange, disabled = false }) {
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const reqId = useRef(0);

  const selectedKeys = useMemo(
    () => new Set(value.map((name) => normalizeKey(name))),
    [value]
  );

  const suggestions = useMemo(
    () =>
      hits.filter((g) => {
        const key = normalizeKey(g.name);
        return key && !selectedKeys.has(key);
      }),
    [hits, selectedKeys]
  );

  useEffect(() => {
    const q = draft.trim();
    if (q.length < MIN_QUERY) {
      setHits([]);
      setSearching(false);
      return undefined;
    }

    const id = ++reqId.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await listWineGrapes({ q, limit: 40 });
        if (reqId.current !== id) return;
        setHits(Array.isArray(data) ? data : []);
      } catch {
        if (reqId.current !== id) return;
        setHits([]);
      } finally {
        if (reqId.current === id) setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [draft]);

  const addGrape = (grape) => {
    if (!grape?.name) return false;
    const name = grape.name;
    if (selectedKeys.has(normalizeKey(name))) {
      setDraft("");
      setHint("");
      setHits([]);
      return true;
    }
    onChange([...(value || []), name]);
    setDraft("");
    setHint("");
    setHits([]);
    return true;
  };

  const resolveAndCommit = async (raw) => {
    const token = String(raw || "").trim();
    if (!token) return false;

    const fromHits = findExact(hits, token);
    if (fromHits) return addGrape(fromHits);

    if (suggestions.length === 1 && normalizeKey(suggestions[0].name).startsWith(normalizeKey(token))) {
      return addGrape(suggestions[0]);
    }

    try {
      setSearching(true);
      const data = await listWineGrapes({ q: token, limit: 40 });
      const list = Array.isArray(data) ? data : [];
      const exact = findExact(list, token);
      if (exact) return addGrape(exact);
      setHint(`Ukendt drue: “${token}”`);
      return false;
    } catch {
      setHint("Kunne ikke søge efter druer");
      return false;
    } finally {
      setSearching(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      if (suggestions.length === 1) {
        addGrape(suggestions[0]);
        return;
      }
      resolveAndCommit(draft);
      return;
    }
    if (event.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const qLen = draft.trim().length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.75,
          minHeight: 48,
        }}
      >
        {value.map((name) => (
          <Chip
            key={name}
            label={name}
            size="small"
            variant="outlined"
            onDelete={disabled ? undefined : () => onChange(value.filter((g) => g !== name))}
            sx={{ borderRadius: 1 }}
          />
        ))}
        <TextField
          value={draft}
          disabled={disabled}
          onChange={(e) => {
            setDraft(e.target.value);
            if (hint) setHint("");
          }}
          onKeyDown={onKeyDown}
          placeholder={value.length ? "Tilføj drue…" : "Skriv mindst 3 bogstaver…"}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            endAdornment: searching ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null,
          }}
          sx={{ flex: 1, minWidth: 140 }}
        />
      </Box>
      {hint ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {hint}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Søg i databasen (min. 3 bogstaver). Enter eller komma låser en chip — mellemrum er OK i
          navne som Cabernet Franc.
        </Typography>
      )}
      {qLen >= MIN_QUERY && suggestions.length ? (
        <Paper variant="outlined" sx={{ mt: 0.75, maxHeight: 220, overflow: "auto" }}>
          <List dense disablePadding>
            {suggestions.map((g) => (
              <ListItemButton
                key={g.wine_grape_uuid || g.name}
                onClick={() => addGrape(g)}
              >
                <ListItemText primary={g.name} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      ) : null}
      {qLen >= MIN_QUERY && !searching && suggestions.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Ingen druer matcher “{draft.trim()}”
        </Typography>
      ) : null}
    </Box>
  );
}

export default GrapeChipsField;
